import { getDayJs } from '~/services/TimeService';

type RelevantDateContext = {
  orderCreatedAt?: string;
};

// Union type for all order items that could have dates
// Similar pattern to ItemsStatus.tsx and uses App.AnyItem which includes BedbankItem
type AnyOrderItem =
  | App.AnyItem // Includes OrderItem | OrderDefinitions.BedbankItem
  | App.CustomOfferOrderItem
  | App.CruiseOrderItem
  | App.OrderTourItem
  | App.ExperienceItem
  | App.FlightItem
  | App.MerchandiseItem;

// Type guards for narrowing item types
function isOrderItem(item: AnyOrderItem): item is App.OrderItem {
  return 'reservation' in item && 'type' in item && typeof item.type === 'string';
}

function isBedbankItem(item: AnyOrderItem): boolean {
  return 'check_in' in item && 'check_out' in item && !('reservation' in item && item.reservation);
}

function isTourItem(item: AnyOrderItem): item is App.OrderTourItem {
  return 'start_date' in item && 'tour_name' in item;
}

function isCruiseItem(item: AnyOrderItem): item is App.CruiseOrderItem {
  return 'departure_id' in item && 'arrival_date' in item;
}

function isFlightItem(item: AnyOrderItem): item is App.FlightItem {
  return 'journey' in item && item.journey !== undefined;
}

function isExperienceItem(item: AnyOrderItem): item is App.ExperienceItem {
  return 'ticket' in item && 'title' in item;
}

function isCustomOfferItem(item: AnyOrderItem): item is App.CustomOfferOrderItem {
  return 'custom_offer' in item && 'fk_custom_offer_id' in item;
}

function isMerchandiseItem(item: AnyOrderItem): item is App.MerchandiseItem {
  return 'display_cache' in item && item.display_cache !== undefined;
}

export function getOrderItemRelevantDate(item: AnyOrderItem, ctx: RelevantDateContext = {}): string | undefined {
  // Accommodation items - matches extractOrderSummary logic
  // Check for check_in or start_date on reservation
  if (isOrderItem(item) && item.reservation) {
    const reservation = item.reservation as App.OrderItemReservation & {
      check_in?: string;
      start_date?: string;
      pickUp?: { date?: string };
    };
    const checkIn = reservation.check_in || reservation.start_date;
    if (checkIn) {
      return getDayJs(checkIn).format('YYYY-MM-DD');
    }
    // Car hire - check pickUp date
    if (reservation.pickUp?.date) {
      return getDayJs(reservation.pickUp.date).format('YYYY-MM-DD');
    }
  }

  // Bedbank items - matches extractOrderSummary (check_in)
  if (isBedbankItem(item) && item.check_in) {
    return getDayJs(item.check_in).format('YYYY-MM-DD');
  }

  // Tour items - matches extractOrderSummary (start_date directly on item)
  if (isTourItem(item) && item.start_date) {
    const startDate = item.start_date instanceof Date ? item.start_date : new Date(item.start_date);
    return getDayJs(startDate).format('YYYY-MM-DD');
  }

  // Cruise items - matches extractOrderSummary (departure_date)
  // Note: departure_date exists at runtime but isn't in the type definition
  if (isCruiseItem(item)) {
    const cruiseItem = item as App.CruiseOrderItem & { departure_date?: string; offer_name?: string };
    if (cruiseItem.departure_date) {
      return getDayJs(cruiseItem.departure_date).format('YYYY-MM-DD');
    }
  }

  // Flight items - matches extractOrderSummary logic
  // Get earliest departing flight date
  if (isFlightItem(item) && item.journey?.departing?.flights?.length) {
    const flights = item.journey.departing.flights;
    const earliestDate = new Date(Math.min(...flights.map((flight) => new Date(flight.departing_date).getTime())));
    return getDayJs(earliestDate).format('YYYY-MM-DD');
  }

  // Experience items - matches extractOrderSummary (ticket.date)
  if (isExperienceItem(item) && item.ticket?.date) {
    const ticketDate = item.ticket.date;
    return getDayJs(ticketDate).format('YYYY-MM-DD');
  }

  // Insurance items - matches extractOrderSummary (start_date)
  // Insurance items are in a separate array, but some might appear as OrderItem with type insurance
  if (isOrderItem(item) && item.type === 'insurance') {
    const insuranceItem = item as App.OrderItem & { start_date?: string };
    if (insuranceItem.start_date) {
      return getDayJs(insuranceItem.start_date).format('YYYY-MM-DD');
    }
  }

  // Custom offer items - matches extractOrderSummary
  if (isCustomOfferItem(item) && item.custom_offer?.items?.[0]?.start_date) {
    return getDayJs(item.custom_offer.items[0].start_date).format('YYYY-MM-DD');
  }

  // Merchandise shipping window - fallback for items without dates
  // Merchandise items use display_cache.seller, not snapshot
  if (isMerchandiseItem(item) && item.display_cache?.seller && ctx.orderCreatedAt) {
    // Note: shipping.maxDays might not be in the type, but can exist at runtime
    const merchandiseItem = item as App.MerchandiseItem & {
      display_cache: { seller: { shipping?: { maxDays?: number } } };
    };
    const maxDays = merchandiseItem.display_cache?.seller?.shipping?.maxDays ?? 0;
    return getDayJs(ctx.orderCreatedAt).add(maxDays, 'days').format('YYYY-MM-DD');
  }

  return undefined;
}
